import {useCallback} from 'react';
import {toast} from 'sonner';
import {isSafari} from 'react-device-detect';
import {useTranslation} from 'react-i18next';
import {isDOMException} from './utils';
import {useErrorModal, useEvent} from '../../hooks';
import {ANALYTIC_EVENTS, Analytics} from '../../analytics';

const GET_USER_MEDIA_ERROR = 'getUserMedia is not implemented in this browser';

type Props = {onError: () => void};
function useErrorHandler({onError}: Props) {
  const {t} = useTranslation();
  const handleError = useEvent(onError);
  const {ErrorModal, displayError} = useErrorModal();

  const handleUserMediaError = useCallback(
    (error: string | DOMException | Error) => {
      console.error(error);
      let errorText = '';

      if ((error as Error)?.message === GET_USER_MEDIA_ERROR) {
        errorText = t('camera_errors.chekin_cant_use_your_camera');
        displayError({
          title: errorText,
          message: t('camera_errors.please_try_a_different_browser'),
        });
      } else if (isDOMException(error)) {
        switch (error.name) {
          case 'PermissionDeniedError':
          case 'NotAllowedError':
            errorText = `${t('camera_errors.permissions_denied')}. ${t(
              'camera_errors.camera_permissions_denied',
            )}`;
            toast.error(errorText);
            break;
          case 'NotFoundError':
            errorText = t('camera_errors.no_camera_device_found');
            toast.error(errorText);
            break;
          case 'NotReadableError':
          case 'AbortError':
            errorText = t('camera_errors.chekin_cant_use_your_camera');
            displayError({
              title: errorText,
              message: `${t('close_other_application_that_may_be_using_your_camera')}.
                ${t('if_you_use_external_camera_disconnect_and_reconnect')}.
                ${t('close_the_browser_re_open_it')}.`,
            });
            break;
          case 'OverconstrainedError':
            errorText = t('camera_errors.constraints_camera_error');

            if (isSafari) {
              displayError({
                title: errorText,
                message: `${t('camera_errors.safari_camera_error')}. ${t(
                  'camera_errors.try_chrome_or_firefox',
                )}.`,
              });
            } else {
              toast.error(errorText);
            }
            break;
          default:
            errorText = t('camera_errors.chekin_cant_use_your_camera');
            displayError({
              title: errorText,
              message: `${t('camera_errors.please_try_a_different_browser')} ${error}`,
            });
        }
      } else {
        errorText = t('camera_errors.chekin_cant_use_your_camera');
        displayError({
          title: errorText,
          message: `${t('camera_errors.please_try_a_different_browser')} ${error}`,
        });
      }

      Analytics.track(ANALYTIC_EVENTS.ivTakingPhotoError, {
        'Error message': errorText,
        Browser: isSafari ? 'Safari' : 'Other',
        'Error Name': isDOMException(error) ? error.name : 'Unknown',
      });
      handleError();
    },
    [t, displayError, handleError],
  );

  return {handleUserMediaError, ErrorModal};
}

export {useErrorHandler};
